import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import API from '../../../../services/API'
import { GlobalContext, TenantSettingsContext } from '../../../../App'
import { PayrollContext } from './Payroll'
// @MUI
import {
  Alert,
  AlertTitle,
  Avatar,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import LogsComponent from '../../../../components/Logs/LogsComponent'
import { formatTravelSummary } from '../../../../helpers/shiftTravel'
import { getCurrencySymbol } from '../../../../helpers/currency'

const Row = ({ title, value, chip, ...props }) => {
  if (!title && !value && !props.children) return null
  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      alignItems="flex-start"
      justifyContent="space-between"
    >
      <Typography flex={1} fontWeight={100} variant="body2">
        {title}
      </Typography>
      <Stack flex={1} alignItems="flex-end">
        {props.children ? (
          props.children
        ) : (
          <Stack direction="row" alignItems="center" gap={1}>
            {chip && chip}
            {value && (
              <Typography
                flex={1}
                fontWeight={800}
                variant="body2"
                textAlign="right"
              >
                {value}
              </Typography>
            )}
          </Stack>
        )}
      </Stack>
    </Stack>
  )
}

const getShiftWorkerDetails = (shift) => {
  const userAccount =
    shift?.assignedTo?.userAccount ?? shift?.assignedSubcontractor?.userAccount
  const name = [userAccount?.firstName, userAccount?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim()

  return {
    name:
      name ||
      (shift?.assignedSubcontractor || shift?.assignedToSubcontractor
        ? 'Contractor'
        : 'Unassigned'),
    photograph: userAccount?.photograph,
    clinicalRole:
      shift?.assignedTo?.clinicalRole ??
      shift?.assignedSubcontractor?.clinicalRole ??
      shift?.clinicalRole,
  }
}

const SelectedShiftDrawer = () => {
  const {
    selectedShift,
    setSelectedShift,
    loadShifts,
    handleMarkShiftsAsPaid,
  } = useContext(PayrollContext)
  const { orgSettings } = useContext(TenantSettingsContext)?.orgConfig
  const { setToast } = useContext(GlobalContext)
  const [shift, setShift] = useState(selectedShift)
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const [verifyingShiftLoading, setVerifyingShiftLoading] = useState(false)
  const [undoLoading, setUndoLoading] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    if (!selectedShift?._id) {
      setShift(null)
      return
    }
    // Reset the loader state whenever another shift is selected, otherwise a
    // previous shift's error (or finished load) leaks into this one
    setShift(selectedShift)
    setLoading(true)
    setFetchError(false)
    API.get(`/overtime/shift-info/${selectedShift._id}`)
      .then((res) => {
        if (res.status === 200) {
          setShift(res.data)
        } else {
          setFetchError(true)
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }, [selectedShift])

  const currencySymbol = getCurrencySymbol(orgSettings?.currency)
  const price = (amount) => `${currencySymbol}${amount ?? 0}`
  const hasIncompleteLegacyClock =
    shift?.clock?.trackingEnabled &&
    (!shift?.clock?.startedAt || !shift?.clock?.endedAt)
  const hasCompleteLegacyClock =
    shift?.clock?.trackingEnabled &&
    shift?.clock?.startedAt &&
    shift?.clock?.endedAt
  const worker = getShiftWorkerDetails(shift)

  const handleVerifyShift = () => {
    setVerifyingShiftLoading(true)
    API.post(`/mark-shift-as-verified/${shift._id}`)
      .then((res) => {
        if (res.status === 200) {
          setShift((prev) => ({
            ...prev,
            isVerified: true,
          }))
          loadShifts()
        } else {
          setToast({
            severity: 'error',
            message:
              res.data?.error ??
              'Unable to verify this shift. Please try again later.',
          })
        }
      })
      .catch(() =>
        setToast({
          severity: 'error',
          message: 'Unable to verify this shift. Please try again later.',
        }),
      )
      .finally(() => setVerifyingShiftLoading(false))
  }

  const handleUnverifyShift = () => {
    setUndoLoading(true)
    API.post(`/unverify-shift/${shift._id}`)
      .then((res) => {
        if (res.status === 200) {
          setShift((prev) => ({
            ...prev,
            isVerified: false,
          }))
          loadShifts()
          setToast({ message: 'Shift Unverified' })
        } else {
          setToast({
            severity: 'error',
            message:
              res.data?.error ??
              'Unable to unverify Shift. Please try again later.',
          })
        }
      })
      .finally(() => setUndoLoading(false))
  }

  const handleUndoMarkAsPaid = () => {
    setUndoLoading(true)
    API.post('/undo-mark-shift-as-paid', {
      shift: shift._id,
    })
      .then((res) => {
        if (res.status === 200) {
          setShift((prev) => ({
            ...prev,
            isPaid: false,
          }))
          loadShifts()
          setToast({ message: 'Shift unmarked as Paid' })
        } else {
          setToast({
            severity: 'error',
            message:
              res.data?.error ??
              'Unable to unmark Shift as paid. Please try again later.',
          })
        }
      })
      .finally(() => setUndoLoading(false))
  }

  return (
    <Drawer
      anchor="right"
      open={!!shift}
      onClose={() => setSelectedShift(null)}
      PaperProps={{
        sx: {
          width: { xs: '80%', sm: 600, lg: 400 },
          top: 8,
          right: 8,
          bottom: 8,
          boxShadow:
            '0 24px 64px rgba(15,25,42,0.18), 0 8px 24px rgba(15,23,42,0.12)',
          border: '1px solid',
          m: 2,
          height: 'calc(100% - 80px)',
          borderRadius: 4,
          overflow: 'auto',
          padding: 2,
        },
      }}
    >
      <Stack gap={2} justifyContent="space-between">
        <Stack gap={2}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="title3">Shift Details</Typography>
            <IconButton onClick={() => setSelectedShift(null)}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>
        {loading && <LinearProgress color="contrast" />}
        {fetchError && (
          <Alert severity="error" icon={false}>
            <AlertTitle color="error">Unable to fetch Shift details</AlertTitle>
            Please check your connection and try again later or contact support
            if the problem persists.
          </Alert>
        )}
        <Stack direction="row" gap={1}>
          <Avatar
            src={worker.photograph}
            sx={{ borderRadius: 2 }}
          />
          <Stack flex={1}>
            <Typography variant="body2">{worker.name}</Typography>
            <Typography variant="caption">{worker.clinicalRole}</Typography>
          </Stack>
          <Chip
            color={
              shift?.ptsDuty
                ? 'anPurple'
                : shift?.event
                  ? 'anRed'
                  : shift?.frontlineDuty
                    ? 'secondary'
                    : 'contrast'
            }
            label={shift?.shiftType}
            variant="outlined"
          />
          <Tooltip
            title={
              shift?.isVerified
                ? `This shift has been verified${shift?.overTime?.decidedAt ? ' automatically' : ''}`
                : shift?.overTime?.required &&
                    shift?.overTime?.status === 'Pending'
                  ? 'This shift has a pending overtime claim'
                  : "This shift has not been verified yet - this could be because the employee hasn't yet clocked out"
            }
          >
            <Chip
              variant="filled"
              color={
                shift?.isVerified
                  ? 'success'
                  : shift?.overTime?.required &&
                      shift?.overTime?.status === 'Pending'
                    ? 'contrast'
                    : 'error'
              }
              label={
                shift?.isVerified
                  ? 'Verified'
                  : shift?.overTime?.required &&
                      shift?.overTime?.status === 'Pending'
                    ? 'Overtime'
                    : 'Unverified'
              }
              onClick={() => {
                shift?.overTime?.status === 'Pending' &&
                  navigate(`/overtime-claims?shift=${shift._id}`)
              }}
              sx={{
                cursor: shift?.overTime?.status === 'Pending' && 'pointer',
              }}
            />
          </Tooltip>
        </Stack>
        <Divider />
        <Stack gap={1}>
          <Row
            title="Shift Date"
            value={moment(shift?.start).format('Do MMM YYYY').toString()}
          />
          <Row
            title="Scheduled"
            value={`${moment(shift?.start).format('HH:mm').toString()} - ${moment(shift?.end).format('HH:mm').toString()}`}
          />
          <Row title="Shift Type" value={shift?.shiftType} />
          <Row title="Clinical Role" value={shift?.clinicalRole} />
          {shift?.eventAssignment && (
            <Row title="Assignment" value={shift.eventAssignment} />
          )}
          <Row title="Note" value={shift?.shiftNote} />
          <Row title="Staff Feedback" value={shift?.staffFeedback} />
        </Stack>
        <Divider />
        <Stack gap={1}>
          <Typography variant="body2" fontWeight={600} fontSize="0.9rem">
            Timings
          </Typography>
          {!shift?.clock?.trackingEnabled ? (
            <Stack gap={1}>
              <Typography flex={1} fontWeight={100} variant="body2">
                Tracking not enabled for this shift.
              </Typography>
              {!shift?.isVerified && (
                <Button
                  fullWidth
                  color="contrast"
                  variant="contained"
                  onClick={() => handleVerifyShift()}
                  disabled={verifyingShiftLoading}
                >
                  Mark shift as Verified
                </Button>
              )}
            </Stack>
          ) : (
            <>
              {hasIncompleteLegacyClock && !shift?.isVerified && (
                <Alert severity="info" variant="outlined">
                  This shift has incomplete clock data, which usually means it
                  was imported from the old system. Payroll staff can verify it
                  manually once the shift details are checked.
                </Alert>
              )}
              {hasCompleteLegacyClock && !shift?.isVerified && (
                <Alert severity="info" variant="outlined">
                  This shift has clock-in and clock-out times but no verification
                  status, which usually means it was imported from the old system.
                  Payroll staff can verify it manually once the shift details are
                  checked.
                </Alert>
              )}
              <Row
                title="Clocked In"
                value={
                  shift?.clock?.startedAt
                    ? moment(shift.clock.startedAt).format('HH:mm').toString()
                    : 'Not recorded'
                }
                chip={
                  shift?.clock?.startStatus && (
                    <Chip
                      size="small"
                      color={
                        shift?.clock?.startStatus === 'Late'
                          ? 'error'
                          : 'contrast'
                      }
                      variant={
                        shift?.clock?.startStatus === 'Late'
                          ? 'filled'
                          : 'outlined'
                      }
                      label={shift?.clock?.startStatus}
                    />
                  )
                }
              />
              {shift?.clock?.lateStartReason && (
                <Row title="Late Start" value={shift.clock.lateStartReason} />
              )}
              <Row
                title="Clocked Out"
                value={
                  shift?.clock?.endedAt
                    ? moment(shift.clock.endedAt).format('HH:mm').toString()
                    : shift?.end && moment(shift.end).isBefore(moment())
                      ? 'Not recorded'
                      : 'Shift in progress'
                }
              />

              {(shift?.clock?.otherReason ??
                shift?.clock?.correctedReason ??
                shift?.clock?.lateEndReason) && (
                <Row
                  title="Late End"
                  value={
                    shift?.clock?.otherReason ??
                    shift?.clock?.correctedReason ??
                    shift?.clock?.lateEndReason
                  }
                />
              )}
              {!shift?.isVerified && (
                <Button
                  fullWidth
                  color="contrast"
                  variant="contained"
                  onClick={() => handleVerifyShift()}
                  disabled={verifyingShiftLoading}
                >
                  Mark shift as Verified
                </Button>
              )}
            </>
          )}
        </Stack>
        <Divider />
        <Stack gap={1}>
          <Typography variant="body2" fontWeight={600} fontSize="0.9rem">
            Pay & Overtime
          </Typography>
          <Row title="Pay Due" value={price(shift?.payDue?.amount)} />
          <Row
            title="Overtime"
            value={price(shift?.payDue?.overTime)}
            chip={
              shift?.overTime?.status && (
                <Chip
                  label={shift.overTime.status}
                  color={
                    shift.overTime.status === 'Rejected'
                      ? 'error'
                      : shift.overTime.status === 'Approved'
                        ? 'success'
                        : 'contrast'
                  }
                  onClick={() => {
                    shift?.overTime?.status === 'Pending' &&
                      navigate(`/overtime-claims?shift=${shift._id}`)
                  }}
                  variant={
                    shift.overTime.status === 'Pending' ? 'filled' : 'outlined'
                  }
                  size="small"
                />
              )
            }
          />
          {(shift?.bonuses ?? []).map((bonus, index) => (
            <Row
              key={index}
              title={bonus.label ? `Bonus — ${bonus.label}` : 'Bonus'}
              value={price(bonus.amount)}
            />
          ))}
          {Number(shift?.payDue?.travel) > 0 && (
            <Row
              title={`Travel time — ${formatTravelSummary(shift?.travel, currencySymbol)}`}
              value={price(shift?.payDue?.travel)}
            />
          )}
          <Row title="Total" value={price(shift?.payDue?.total)} />
          {shift?.isVerified &&
            !shift?.isPaid &&
            !shift?.payRun &&
            shift?.overTime?.status !== 'Pending' && (
              <Button
                variant="contained"
                color="contrast"
                onClick={() => handleMarkShiftsAsPaid([shift._id])}
              >
                Mark shift as paid
              </Button>
            )}
          {shift?.isVerified && !shift?.isPaid && !shift?.payRun && (
            <Button
              variant="outlined"
              color="contrast"
              onClick={() => handleUnverifyShift()}
              disabled={undoLoading}
            >
              Unverify Shift
            </Button>
          )}
          {shift?.isPaid && !shift?.payRun && (
            <Button
              variant="outlined"
              color="contrast"
              onClick={() => handleUndoMarkAsPaid()}
              disabled={undoLoading}
            >
              Undo Mark as Paid
            </Button>
          )}
          {shift?.payRun && (
            <Alert severity="info" variant="outlined">
              This shift is part of pay run{' '}
              {shift.payRun?.reference ?? shift.payRun}
              {shift?.isPaid
                ? ' and has been paid through it, so it cannot be individually unverified or unmarked as paid.'
                : ', so it cannot be unverified. Cancel the pay run from the Pay Runs tab to release it.'}
            </Alert>
          )}
          {shift?.overTime?.required && shift?.overTime?.status == 'Pending' && (
            <Stack gap={1}>
            <Alert severity='error' color='contrast'>Pending overtime claim</Alert>

            <Button
              variant="contained"
              color="contrast"
              onClick={() => {
                shift?.overTime?.status === 'Pending' &&
                  navigate(`/overtime-claims?shift=${shift._id}`)
              }}
            >
              Review Overtime Claim
            </Button>

            </Stack>
          )}
        </Stack>
        <Divider />
        <Stack>
          <Typography variant="body2" fontWeight={600} fontSize="0.9rem">
            Activity
          </Typography>
          <LogsComponent
            hideIfEmpty
            hideTitle
            targetType="shift"
            targetId={selectedShift?._id}
            hideDivider
            disableFiltering
          />
        </Stack>
      </Stack>
    </Drawer>
  )
}

export default SelectedShiftDrawer
